import { useTranslation } from 'react-i18next';
import { Users, UsersRound, Briefcase, GitBranch, KeyRound } from 'lucide-react';
import type { SystemStatistics, SystemCapability, ShowSystemResponse } from '@bliks-io/contracts';
import { useSystem } from '@/api/queries/useSystems';
import { notFoundOrUnknownKey } from '@/api/errors';
import { useAuthStore } from '@/stores/auth-store';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { Card, CardContent, Skeleton, StatCard, cn, Page, PageContent } from '@bliks-io/ui';
import { BackButton } from '@/components/back-button';
import { CapacityStatCard } from '@/components/capacity-stat-card';
import { CriticalDependenciesCard } from './components/CriticalDependenciesCard';
import { ProcessesImpactedCard } from './components/ProcessesImpactedCard';
import { SystemUsersCard } from './components/SystemUsersCard';
import { SystemInteractionsCard } from './components/SystemInteractionsCard';
import { MediaBreakMapCard } from './components/MediaBreakMapCard';
import { SystemDetailHeader } from './components/SystemDetailHeader';
import { SystemDetailSidebar } from './components/SystemDetailSidebar';

interface SystemDetailPageProps {
  systemId: string;
}

function SystemDetailPageError({ slug, error }: { slug: string; error: unknown }) {
  const { t } = useTranslation();
  return (
    <Page>
      <PageContent>
        <div className="flex items-center gap-4 mb-8">
          <BackButton to="/$slug/organization/systems" params={{ slug }} />
        </div>
        <Card>
          <CardContent className="p-6">
            <p className="text-destructive">
              {t(notFoundOrUnknownKey(error, 'organization.systems.notFound'))}
            </p>
          </CardContent>
        </Card>
      </PageContent>
    </Page>
  );
}

function SystemStatisticsCards({
  statistics,
  capabilities
}: {
  statistics: SystemStatistics;
  capabilities?: SystemCapability;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
      <StatCard
        icon={<Users className="w-5 h-5 text-info" />}
        title={t('organization.systems.stats.totalUsers')}
        value={statistics.totalUsers}
        description={t('organization.systems.stats.totalUsersDescription')}
      />
      <StatCard
        icon={<UsersRound className="w-5 h-5 text-info" />}
        title={t('organization.systems.stats.totalOrgFunctions')}
        value={statistics.totalOrgFunctions}
        description={t('organization.systems.stats.totalOrgFunctionsDescription')}
      />
      <StatCard
        icon={<Briefcase className="w-5 h-5 text-info" />}
        title={t('organization.systems.stats.totalRoles')}
        value={statistics.totalRoles}
        description={t('organization.systems.stats.totalRolesDescription')}
      />
      <StatCard
        icon={<GitBranch className="w-5 h-5 text-info" />}
        title={t('organization.systems.stats.totalProcesses')}
        value={statistics.totalProcesses}
        description={t('organization.systems.stats.totalProcessesDescription')}
      />
      {capabilities?.canViewSensitiveFields && (
        <CapacityStatCard capacity={statistics.capacityBinding} />
      )}
    </div>
  );
}

function LicenceSeatsCard({
  licenceSeats,
  totalUsers
}: {
  licenceSeats: number;
  totalUsers: number;
}) {
  const { t } = useTranslation();
  if (licenceSeats <= 0) return null;
  const isOverCapacity = totalUsers > licenceSeats;

  return (
    <StatCard
      icon={<KeyRound className={cn('w-5 h-5', isOverCapacity ? 'text-danger' : 'text-info')} />}
      title={t('organization.systems.stats.licenceSeats')}
      value={`${totalUsers} / ${licenceSeats}`}
      description={t('organization.systems.stats.licenceSeatsUsed')}
      iconClassName={isOverCapacity ? 'bg-danger-surface' : 'bg-info-surface'}
    >
      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn(
            'h-full rounded-full transition-all',
            isOverCapacity ? 'bg-danger-solid' : 'bg-info-solid'
          )}
          style={{
            width: `${Math.min((totalUsers / licenceSeats) * 100, 100)}%`
          }}
        />
      </div>
    </StatCard>
  );
}

function SystemDetailPageSkeleton() {
  return (
    <Page>
      <PageContent>
        <Skeleton className="w-64 h-8 mb-8" />
        {/* Statistics Cards Skeleton */}
        <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-2 lg:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="relative p-5 border rounded-lg shadow-xs bg-card">
              <Skeleton className="absolute w-10 h-10 rounded-md right-4 top-4" />
              <Skeleton className="w-20 h-3 mb-2" />
              <Skeleton className="w-12 h-8 mb-1" />
              <Skeleton className="w-32 h-3" />
            </div>
          ))}
        </div>
      </PageContent>
    </Page>
  );
}

function SystemDetailContentGrid({
  system,
  statistics,
  capabilities
}: {
  system: Pick<ShowSystemResponse['data'], '_id' | 'name' | 'logo' | 'licenceSeats'>;
  statistics: SystemStatistics;
  capabilities?: ShowSystemResponse['capabilities'];
}) {
  const criticalDependencies = statistics.criticalDependencies ?? [];
  const hasCriticalDeps = capabilities?.canViewSensitiveFields && criticalDependencies.length > 0;

  return (
    <div className="grid grid-cols-1 2xl:grid-cols-4 gap-6">
      <div className="2xl:col-start-4 2xl:row-start-1 2xl:row-span-3 space-y-6">
        {system.licenceSeats !== undefined && system.licenceSeats > 0 && (
          <LicenceSeatsCard licenceSeats={system.licenceSeats} totalUsers={statistics.totalUsers} />
        )}
        <SystemDetailSidebar statistics={statistics} />
      </div>

      {hasCriticalDeps && (
        <div className="2xl:col-span-3">
          <CriticalDependenciesCard dependencies={criticalDependencies} />
        </div>
      )}

      <div className="2xl:col-span-3">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className={statistics.mediaBreakMap ? 'lg:col-span-2' : 'lg:col-span-3'}>
            <ProcessesImpactedCard
              processes={statistics.impactedProcesses ?? []}
              canViewSensitiveFields={capabilities?.canViewSensitiveFields}
            />
          </div>
          {statistics.mediaBreakMap && (
            <div className="lg:col-span-1">
              <MediaBreakMapCard mediaBreakMap={statistics.mediaBreakMap} />
            </div>
          )}
        </div>
      </div>

      <div className="2xl:col-span-3">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className={statistics.systemInteractions ? 'lg:col-span-2' : 'lg:col-span-3'}>
            <SystemUsersCard users={statistics.systemUsers ?? []} />
          </div>
          {statistics.systemInteractions && (
            <div className="lg:col-span-1">
              <SystemInteractionsCard
                currentSystem={{ _id: system._id, name: system.name, logo: system.logo }}
                interactions={statistics.systemInteractions}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function SystemDetailPage({ systemId }: SystemDetailPageProps) {
  const { t } = useTranslation();
  const slug = useAuthStore().slug();
  const { data: response, isPending, error } = useSystem(systemId);
  const system = response?.data;
  const capabilities = response?.capabilities;

  useDocumentTitle('pageTitles.systemDetail', { name: system?.name || t('common.loading') });

  if (isPending) {
    return <SystemDetailPageSkeleton />;
  }

  if (error || !system) {
    return <SystemDetailPageError slug={slug} error={error} />;
  }

  return (
    <Page>
      <PageContent>
        <SystemDetailHeader system={system} slug={slug} capabilities={capabilities} />
        {system.statistics && (
          <div className="space-y-6">
            <SystemStatisticsCards statistics={system.statistics} capabilities={capabilities} />
            <SystemDetailContentGrid
              system={system}
              statistics={system.statistics}
              capabilities={capabilities}
            />
          </div>
        )}
      </PageContent>
    </Page>
  );
}
